import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import { Alert, Box, Button, Chip, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, Divider, Grid, IconButton, Tooltip, Typography } from "@mui/material";
import { useCallback, useEffect, useState } from "react";
import { SetupChargeService } from "../../../../shared/services/api/setupCharge/SetupChargeService";
import { ISetupCharge, ISetupChargeHistoryItem, ISetupChargeRequest, TSetupChargeAction } from "../../../../shared/services/api/setupCharge/Types";
import { copyToClipboard, globalMaskDocument } from "../../../../shared/utils";
import { GatewayLogo } from "../../../client/components/createComponents/GatewaySelect";
import { setupChargeFormatCurrency, setupChargeFormatDate } from "../../utils/setupChargeFormat";
import {
	setupChargeEventLabel,
	setupChargeFeePayerLabel,
	setupChargePaymentMethodLabel,
	setupChargeRequestStateColor,
	setupChargeRequestStateLabel,
	setupChargeRequestTypeLabel,
	setupChargeSourceLabel,
	setupChargeStatusColor,
	setupChargeStatusLabel,
} from "../../utils/setupChargeStatus";
import { setupChargeActionState } from "../TableSetupCharge";

interface ModalSetupChargeDetailProps {
	/** Cobrança aberta; `null` mantém o modal fechado. A tela busca o detalhe completo (histórico e solicitações). */
	chargeId: string | null;
	/** Muda quando a lista recarrega (ex.: depois de uma ação), para o detalhe se atualizar junto. */
	refreshKey?: number;
	canManage: boolean;
	onClose: () => void;
	onAction: (action: TSetupChargeAction, charge: ISetupCharge) => void;
}

const ACTION_BUTTONS: { key: TSetupChargeAction; label: string; color?: "error" | "primary" }[] = [
	{ key: "resend", label: "Reenviar link" },
	{ key: "contact_update", label: "Corrigir contato" },
	{ key: "manual_payment", label: "Dar baixa" },
	{ key: "repass", label: "Registrar repasse" },
	{ key: "reprocess", label: "Reprocessar" },
	{ key: "cancel", label: "Cancelar taxa", color: "error" },
];

const Field = ({ label, value }: { label: string; value?: React.ReactNode }) => (
	<Grid item xs={12} sm={6} md={4}>
		<Typography variant="caption" color="textSecondary" display="block">
			{label}
		</Typography>
		<Typography variant="body2" sx={{ wordBreak: "break-word" }}>
			{value === undefined || value === null || value === "" ? "-" : value}
		</Typography>
	</Grid>
);

const Section = ({ title, children }: { title: string; children: React.ReactNode }) => (
	<Box mb={2}>
		<Typography variant="subtitle2" color="secondary" fontWeight="bold" mb={1}>
			{title}
		</Typography>
		<Grid container spacing={1.5}>
			{children}
		</Grid>
	</Box>
);

export const ModalSetupChargeDetail = ({ chargeId, refreshKey = 0, canManage, onClose, onAction }: ModalSetupChargeDetailProps) => {
	const [charge, setCharge] = useState<ISetupCharge | null>(null);
	const [isLoading, setIsLoading] = useState(false);
	const [errorMessage, setErrorMessage] = useState<string | null>(null);
	const [copied, setCopied] = useState(false);

	const load = useCallback(async (id: string) => {
		setIsLoading(true);
		const result = await SetupChargeService.getById(id);
		setIsLoading(false);
		if (result instanceof Error) {
			setErrorMessage(result.message);
			return;
		}
		setErrorMessage(null);
		setCharge(result);
	}, []);

	useEffect(() => {
		if (!chargeId) {
			setCharge(null);
			setErrorMessage(null);
			return;
		}
		load(chargeId);
	}, [chargeId, refreshKey, load]);

	if (!chargeId) return null;

	const history: ISetupChargeHistoryItem[] = charge?.history ?? [];
	const requests: ISetupChargeRequest[] = charge?.requests ?? [];
	const snapshot = charge?.snapshot;
	const hasPendingRequest = requests.some((request) => request.state === "received" || request.state === "processing");
	const attention: { label: string; color: "error" | "warning" }[] = [];
	if (charge?.duplicatePayment) attention.push({ label: "PAGAMENTO_DUPLICADO", color: "error" });
	if (charge?.status === "failed") attention.push({ label: "Cobrança com falha", color: "error" });
	if (requests.some((request) => request.state === "dead")) attention.push({ label: "Solicitação esgotou as tentativas (dead)", color: "error" });
	if (hasPendingRequest) attention.push({ label: "Solicitação em andamento", color: "warning" });

	const handleCopy = async () => {
		if (!charge?.paymentLink) return;
		await copyToClipboard(charge.paymentLink);
		setCopied(true);
		setTimeout(() => setCopied(false), 2000);
	};

	return (
		<Dialog open onClose={onClose} fullWidth maxWidth="md" scroll="paper">
			<DialogTitle>
				<Box display="flex" alignItems="center" gap={1} flexWrap="wrap">
					Taxa avulsa
					{charge && <Chip size="small" label={setupChargeStatusLabel(charge.status)} color={setupChargeStatusColor(charge.status)} variant="outlined" />}
					{attention.map((item) => (
						<Chip key={item.label} size="small" label={item.label} color={item.color} />
					))}
					{isLoading && <CircularProgress size={18} />}
				</Box>
			</DialogTitle>
			<DialogContent dividers>
				{errorMessage && (
					<Alert severity="error" sx={{ mb: 2 }}>
						{errorMessage}
					</Alert>
				)}

				{charge && (
					<>
						<Section title="Pagamento">
							<Field label="Valor cobrado" value={setupChargeFormatCurrency(charge.amount)} />
							<Field label="Taxa do gateway" value={setupChargeFormatCurrency(charge.gatewayFee)} />
							<Field label="Valor líquido" value={setupChargeFormatCurrency(charge.netAmount)} />
							<Field label="Gateway" value={charge.gateway ? <GatewayLogo gateway={charge.gateway} /> : ""} />
							<Field label="Forma de pagamento" value={setupChargePaymentMethodLabel(charge.paymentMethod)} />
							<Field label="Código no parceiro" value={charge.externalId} />
							<Grid item xs={12}>
								<Typography variant="caption" color="textSecondary" display="block">
									Link de pagamento
								</Typography>
								{charge.paymentLink ? (
									<Box display="flex" alignItems="center" gap={1}>
										<Typography variant="body2" sx={{ wordBreak: "break-all" }}>
											{charge.paymentLink}
										</Typography>
										<Tooltip title={copied ? "Copiado" : "Copiar link"}>
											<IconButton size="small" onClick={handleCopy}>
												<ContentCopyIcon fontSize="small" />
											</IconButton>
										</Tooltip>
									</Box>
								) : (
									<Typography variant="body2">-</Typography>
								)}
							</Grid>
						</Section>

						<Section title="Datas">
							<Field label="Criação" value={setupChargeFormatDate(charge.createdAt, true)} />
							<Field label="Vencimento" value={setupChargeFormatDate(charge.expiresAt)} />
							<Field label="Pagamento" value={setupChargeFormatDate(charge.paidAt, true)} />
							<Field label="Cancelamento" value={setupChargeFormatDate(charge.canceledAt, true)} />
							<Field label="Motivo do cancelamento" value={charge.cancelReason} />
						</Section>

						<Section title="Repasse">
							<Field label="Percentual" value={charge.repassPercent !== undefined && charge.repassPercent !== null ? `${charge.repassPercent}%` : ""} />
							<Field label="Esperado" value={setupChargeFormatCurrency(charge.expectedRepass)} />
							<Field label="Situação" value={charge.repassed ? "Repassado" : "Pendente"} />
							<Field label="Valor repassado" value={setupChargeFormatCurrency(charge.repassAmount)} />
							<Field label="Data do repasse" value={setupChargeFormatDate(charge.repassedAt)} />
							<Field label="Repassado por" value={charge.repassedBy} />
						</Section>

						<Section title="Imobiliária e origem">
							<Field label="Imobiliária" value={charge.clientName} />
							<Field label="Documento" value={globalMaskDocument(charge.clientDocument ?? "")} />
							<Field label="Origem" value={charge.partner === "panel" ? "Painel" : charge.partner} />
							{charge.analysisId && <Field label="Análise" value={charge.analysisId} />}
							{charge.proposalId && <Field label="Proposta" value={charge.proposalId} />}
						</Section>

						<Section title="Pagador">
							<Field label="Nome" value={charge.payerName} />
							<Field label="Documento" value={globalMaskDocument(charge.payerDocument ?? "")} />
							<Field label="E-mail" value={charge.payerEmail} />
							<Field label="Celular" value={charge.payerPhone} />
						</Section>

						<Section title="Imóvel">
							<Field label="CEP" value={charge.addressZipcode} />
							<Field label="Endereço" value={[charge.addressStreet, charge.addressNumber].filter(Boolean).join(", ")} />
							<Field label="Complemento" value={charge.addressComplement} />
							<Field label="Bairro" value={charge.addressDistrict} />
							<Field label="Cidade / UF" value={[charge.addressCity, charge.addressState].filter(Boolean).join(" / ")} />
						</Section>

						<Section title="Parâmetros vigentes na criação">
							<Field label="% de repasse" value={snapshot?.repassPercent !== undefined && snapshot?.repassPercent !== null ? `${snapshot.repassPercent}%` : ""} />
							<Field label="Gateway" value={snapshot?.gateway} />
							<Field label="Validade do link (dias)" value={snapshot?.linkValidityDays} />
							<Field label="Máximo de parcelas" value={snapshot?.maxInstallments} />
							<Field label="Quem arca com a taxa" value={setupChargeFeePayerLabel(snapshot?.feePayer)} />
							<Field label="Formas de pagamento" value={(snapshot?.paymentMethods ?? []).map(setupChargePaymentMethodLabel).join(", ")} />
						</Section>

						<Divider sx={{ my: 2 }} />

						<Section title="Solicitações">
							{requests.length === 0 && (
								<Grid item xs={12}>
									<Typography variant="body2" color="textSecondary">
										Nenhuma solicitação registrada.
									</Typography>
								</Grid>
							)}
							{requests.map((request) => (
								<Grid item xs={12} key={request.requestId}>
									<Box display="flex" alignItems="center" gap={1} flexWrap="wrap">
										<Chip size="small" label={setupChargeRequestTypeLabel(request.type)} variant="outlined" />
										<Chip size="small" label={setupChargeRequestStateLabel(request.state)} color={setupChargeRequestStateColor(request.state)} />
										<Typography variant="caption" color="textSecondary">
											{setupChargeFormatDate(request.createdAt, true)} · {request.requestId}
										</Typography>
									</Box>
									{(request.resultCode || request.resultMessage) && (
										<Typography variant="body2" color={request.state === "completed" ? "textSecondary" : "error"} mt={0.5}>
											{[request.resultCode, request.resultMessage].filter(Boolean).join(": ")}
										</Typography>
									)}
								</Grid>
							))}
						</Section>

						<Section title="Histórico">
							{history.length === 0 && (
								<Grid item xs={12}>
									<Typography variant="body2" color="textSecondary">
										Sem eventos registrados.
									</Typography>
								</Grid>
							)}
							{history.map((item) => (
								<Grid item xs={12} key={item.id}>
									<Box display="flex" gap={2} alignItems="baseline" flexWrap="wrap">
										<Typography variant="caption" color="textSecondary" sx={{ minWidth: 130 }}>
											{setupChargeFormatDate(item.createdAt, true)}
										</Typography>
										<Typography variant="body2" fontWeight="bold">
											{setupChargeEventLabel(item.eventType)}
										</Typography>
										<Typography variant="caption" color="textSecondary">
											{[setupChargeSourceLabel(item.source), item.userName].filter(Boolean).join(" · ")}
											{item.fromStatus || item.toStatus ? ` · ${setupChargeStatusLabel(item.fromStatus ?? "")} → ${setupChargeStatusLabel(item.toStatus ?? "")}` : ""}
										</Typography>
									</Box>
								</Grid>
							))}
						</Section>
					</>
				)}
			</DialogContent>
			<DialogActions sx={{ flexWrap: "wrap", gap: 0.5 }}>
				{canManage &&
					charge &&
					ACTION_BUTTONS.map(({ key, label, color }) => {
						const state = setupChargeActionState(charge, key);
						const button = (
							<Button key={key} size="small" color={color ?? "primary"} variant="outlined" disabled={!state.allowed} onClick={() => onAction(key, charge)}>
								{label}
							</Button>
						);
						return state.allowed || !state.message ? (
							button
						) : (
							<Tooltip key={key} title={state.message}>
								<span>{button}</span>
							</Tooltip>
						);
					})}
				<Box flexGrow={1} />
				<Button onClick={onClose}>Fechar</Button>
			</DialogActions>
		</Dialog>
	);
};
